import type { DeviceMasterPatch } from '../pages/Raiz/components/DeviceMasterDialog'
// ============================================
// RAIZ IoT — client da read API do dashboard de gestão
// ============================================

import { apiGet, apiPost, apiPatch, apiPut, apiDelete, ApiError } from './api'
import { API_ENDPOINTS } from '../config/api'
import type {
  RaizDevicesResponse, RaizDeviceDetail, RaizRange,
  RaizCalibration, RaizSetpoint, CalibrationPayload, SetpointPayload, RaizReportData,
  RaizUnit, RaizUnitDetail, UnitPayload, RaizUnitReport,
  RaizSite, SitePayload, RaizUnitBandVigencia, RaizUnitLevel,
  RaizExcursion, RaizGlobalExcursion, RaizExcursionsSummary, ExcursionDispositionPayload, RaizExcursionAuditEntry, RaizAdminAuditEntry, RaizClockDriftAlert,
  RaizConfigPushPayload, RaizConfigPushStatus, IngestHealth, RaizExportLogEntry,
  RaizDeviceKey, DeviceKeyPayload, GeoJsonPoint, FloorPosition,
  CredentialsStatusRow, DeviceCredentialsState, InstallPasswordStatus, CredentialField, RevealField,
  RaizAttestResponse, RaizVerifyResponse, RaizOwnedAttestation, HeatmapResult,
  FirmwareRelease, FirmwareReleaseCanary, FirmwareDeviceStatus, FirmwareAuditEntry, FirmwareUploadParams,
  FirmwareUploadResult, BenchFlashPayload, FactoryImage, FirmwareDeviceOfferResume,
  RaizAlarmProfile, RaizAlarmRule, RaizUnitAlarmProfile,
  UnitsImportPayload, UnitsImportResponse,
  BatteryFleet, BatteryCurve, BatteryLabWindow,
} from '../pages/Raiz/types'

/** Limite de upload da planta (contrato do servidor: corpo binário ≤ 4 MB). */
export const FLOORPLAN_MAX_BYTES = 4 * 1024 * 1024
/** Content-Types aceitos pelo PUT /units/:id/floorplan. */
export const FLOORPLAN_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp']

export const FLOORPLAN_TOO_LARGE_MSG = 'Imagem muito grande — o limite da planta é 4 MB.'
export const FLOORPLAN_BAD_TYPE_MSG = 'Formato não suportado — envie a planta em PNG, JPEG ou WebP.'

/** Mensagem do 409 no upload de release (versão+board já existem). */
export const FIRMWARE_RELEASE_EXISTS_MSG = 'Já existe uma release para esta versão e board.'
/** Mensagem do 413 no upload de release (binário acima do limite do servidor). */
export const FIRMWARE_RELEASE_TOO_LARGE_MSG = 'Binário muito grande — excede o limite do servidor.'
/** Mensagem do 400 no upload de release (semver/board inválidos). */
export const FIRMWARE_RELEASE_INVALID_MSG = 'Dados inválidos — verifique a versão (X.Y.Z) e o board.'

/** Mensagem do 409 no upload da imagem de fábrica (release já tem uma — write-once). */
export const FACTORY_EXISTS_MSG = 'Esta release já tem imagem de fábrica anexada.'
/** Mensagem do 413 no upload da imagem de fábrica. */
export const FACTORY_TOO_LARGE_MSG = 'Imagem de fábrica muito grande — excede o limite do servidor.'
/** Mensagem do 400 no upload da imagem de fábrica. */
export const FACTORY_INVALID_MSG = 'Imagem de fábrica inválida.'
/** Mensagem do 404 ao baixar a imagem de fábrica (release sem imagem anexada). */
export const FACTORY_MISSING_MSG = 'Esta release não tem imagem de fábrica.'

/** Monta a query string filtrando undefined/'' — mesmo comportamento do api.get do Aura. */
const withQuery = (endpoint: string, params: Record<string, string | number | boolean | undefined>): string => {
  const filtered: Record<string, string> = {}
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') filtered[k] = String(v)
  }
  const queryString = new URLSearchParams(filtered).toString()
  return queryString ? `${endpoint}?${queryString}` : endpoint
}

export const raizService = {
  /** Lista de devices RAIZ com presença + resumo. */
  getDevices: (): Promise<RaizDevicesResponse> =>
    apiGet<RaizDevicesResponse>(API_ENDPOINTS.RAIZ_DEVICES),

  /** Detalhe de um device: overview + stats + leituras na janela. */
  getDevice: (mac: string, range: RaizRange = '24h'): Promise<RaizDeviceDetail> =>
    apiGet<RaizDeviceDetail>(withQuery(API_ENDPOINTS.RAIZ_DEVICE(mac), { range })),

  /** Define (Point) ou remove (null) a localização geográfica do device. */
  setDeviceLocation: (mac: string, geojson: GeoJsonPoint | null): Promise<void> =>
    apiPatch<void>(API_ENDPOINTS.RAIZ_DEVICE_LOCATION(mac), { geojson }),

  /**
   * Renomeia o sensor para a empresa. Escreve só display_name — o label continua
   * sendo do firmware. `null` limpa o nome e volta a exibir o label.
   */
  /**
   * Cadastro mestre do instrumento (HW-004/006). PATCH PARCIAL: só as chaves
   * presentes são gravadas — chave ausente não é tocada no servidor.
   */
  updateDeviceMaster: (mac: string, patch: DeviceMasterPatch): Promise<void> =>
    apiPatch<void>(API_ENDPOINTS.RAIZ_DEVICE(mac), patch),

  /** Trilha do cadastro mestre (1829) — quem mudou o quê no instrumento. */
  getDeviceMasterAudit: (mac: string): Promise<RaizAdminAuditEntry[]> =>
    apiGet<RaizAdminAuditEntry[]>(API_ENDPOINTS.RAIZ_DEVICE_MASTER_AUDIT(mac)),

  /** Desvio de relógio vivo (TIME-005) — aviso in-app, independente de webhook. */
  listClockDrift: (): Promise<RaizClockDriftAlert[]> =>
    apiGet<RaizClockDriftAlert[]>(API_ENDPOINTS.RAIZ_CLOCK_DRIFT),

  /** Config pelo beacon (raiz_iot#94): status do push × eco e novo push (admin). */
  getConfigPush: (mac: string): Promise<RaizConfigPushStatus> =>
    apiGet<RaizConfigPushStatus>(API_ENDPOINTS.RAIZ_DEVICE_CONFIG_PUSH(mac)),
  /**
   * `supersedesSeq` = o push pendente que o admin viu e aceita substituir
   * (issue #114). null = "não há pendente" — se houver, o servidor responde
   * 409 config_push_conflict em vez de descartá-lo em silêncio.
   */
  pushConfig: (
    mac: string, payload: RaizConfigPushPayload, supersedesSeq: number | null = null,
    opts: { confirmCarteiro?: boolean } = {},
  ): Promise<{ seq: number }> => {
    const q = new URLSearchParams()
    if (supersedesSeq != null) q.set('supersedes_seq', String(supersedesSeq))
    // Carteiro/bateria/manutenção: ciência explícita, na query (o corpo viaja para o device).
    if (opts.confirmCarteiro === true) q.set('confirm_carteiro', '1')
    const qs = q.toString()
    return apiPost<{ seq: number }>(`${API_ENDPOINTS.RAIZ_DEVICE_CONFIG_PUSH(mac)}${qs !== '' ? `?${qs}` : ''}`, payload)
  },

  /**
   * Mapa de calor temporal (dia × hora local) do device. `tz` = fuso IANA do
   * operador (padrões de hora do dia só existem em hora local). from/to ISO;
   * sem eles o servidor usa os últimos 30 dias.
   */
  getHeatmap: (mac: string, params: { from?: string, to?: string, tz: string }): Promise<HeatmapResult> =>
    apiGet<HeatmapResult>(withQuery(API_ENDPOINTS.RAIZ_DEVICE_HEATMAP(mac), params)),

  /** Histórico de calibrações do device. */
  getCalibrations: (mac: string): Promise<RaizCalibration[]> =>
    apiGet<RaizCalibration[]>(API_ENDPOINTS.RAIZ_DEVICE_CALIBRATIONS(mac)),

  /** Registra uma calibração (append-only). */
  createCalibration: (mac: string, payload: CalibrationPayload): Promise<{ id: number }> =>
    apiPost<{ id: number }>(API_ENDPOINTS.RAIZ_DEVICE_CALIBRATIONS(mac), payload),

  /** Histórico de setpoints do device. */
  getSetpoints: (mac: string): Promise<RaizSetpoint[]> =>
    apiGet<RaizSetpoint[]>(API_ENDPOINTS.RAIZ_DEVICE_SETPOINTS(mac)),

  /** Define um setpoint (versionado, append-only). */
  createSetpoint: (mac: string, payload: SetpointPayload): Promise<{ id: number }> =>
    apiPost<{ id: number }>(API_ENDPOINTS.RAIZ_DEVICE_SETPOINTS(mac), payload),

  /** Relatório compliance do device no período (datas YYYY-MM-DD). */
  getReport: (mac: string, from: string, to: string): Promise<RaizReportData> =>
    apiGet<RaizReportData>(withQuery(API_ENDPOINTS.RAIZ_DEVICE_REPORT(mac), { from, to })),

  /** Emite um selo de autenticidade para o relatório do device no período. */
  attestReport: (mac: string, from: string, to: string): Promise<RaizAttestResponse> =>
    apiPost<RaizAttestResponse>(API_ENDPOINTS.RAIZ_DEVICE_REPORT_ATTEST(mac), { from, to }),

  /** Selos já emitidos pela empresa para o device (REP-013 — cadeia de reemissão). */
  listAttestations: (mac: string): Promise<{ attestations: RaizOwnedAttestation[], truncated: boolean }> =>
    apiGet<{ attestations: RaizOwnedAttestation[], truncated: boolean }>(API_ENDPOINTS.RAIZ_DEVICE_ATTESTATIONS(mac)),

  /** Verifica um selo pelo id (endpoint público — funciona deslogado). */
  /**
   * Verificação pública de selo. O backend responde 404 com { found:false }
   * quando o selo não existe — e o apiFetch transforma TODO !ok em exceção, o
   * que fazia o "selo inexistente" chegar na página como erro de sistema
   * ("Não foi possível verificar o selo agora"), em vez do card "Selo não
   * encontrado". Para o auditor de um documento FORJADO, é a diferença entre
   * "a RAIZ está fora do ar" e "este documento não é autêntico".
   *
   * O 404 é semanticamente correto no HTTP e fica; a tradução para o domínio
   * ({ found:false }) é feita aqui. O id é normalizado para maiúsculas: os ids
   * são base32 maiúsculo, e um id digitado à mão em minúsculas 404ava.
   */
  verifyAttestation: async (id: string): Promise<RaizVerifyResponse> => {
    try {
      return await apiGet<RaizVerifyResponse>(API_ENDPOINTS.RAIZ_VERIFY(id.trim().toUpperCase()))
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return { found: false }
      throw err
    }
  },

  /**
   * Baixa o CSV do relatório do device no período e dispara o download no
   * browser. Nome do arquivo: raiz-<mac-sem-dois-pontos>-<from>_<to>.csv.
   * Usa fetch direto (blob) com credenciais — o wrapper JSON não serve aqui.
   */
  downloadReportCsv: async (mac: string, from: string, to: string): Promise<void> => {
    const url = withQuery(API_ENDPOINTS.RAIZ_DEVICE_REPORT_CSV(mac), { from, to })
    let res: Response
    try {
      res = await fetch(url, { credentials: 'include', headers: { Accept: 'text/csv' } })
    } catch {
      throw new ApiError(0, 'Sem conexão com o servidor.')
    }
    if (!res.ok) {
      // Tenta extrair mensagem do corpo (JSON de erro); senão, genérica por status.
      let message = ''
      try {
        const text = await res.text()
        if (text) {
          try {
            const parsed = JSON.parse(text) as { message?: unknown }
            if (typeof parsed?.message === 'string') message = parsed.message
          } catch { /* corpo não-JSON */ }
        }
      } catch { /* corpo ilegível */ }
      if (!message) {
        message = res.status === 404
          ? 'Exportação CSV indisponível para este relatório.'
          : res.status >= 500
            ? 'Erro no servidor ao gerar o CSV.'
            : 'Falha ao baixar o CSV.'
      }
      throw new ApiError(res.status, message)
    }
    const blob = await res.blob()
    const href = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = href
    a.download = `raiz-${mac.replace(/:/g, '')}-${from}_${to}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(href)
  },

  /** Saúde da ingestão (devices desconhecidos batendo + resumo 24h). */
  getIngestHealth: (): Promise<IngestHealth> =>
    apiGet<IngestHealth>(API_ENDPOINTS.RAIZ_INGEST_HEALTH),

  /** Trilha de extração de dados (1832) — quem exportou o quê, mais recente primeiro. */
  getExports: (): Promise<RaizExportLogEntry[]> =>
    apiGet<RaizExportLogEntry[]>(API_ENDPOINTS.RAIZ_EXPORTS),

  // ── Gestão/aprovação de chaves de device (admin) ───────────────────────────
  /** Lista as chaves cadastradas (sem o segredo) + presença + flag de chave compartilhada. */
  listDeviceKeys: (): Promise<RaizDeviceKey[]> =>
    apiGet<RaizDeviceKey[]>(API_ENDPOINTS.RAIZ_DEVICE_KEYS),

  /** Cadastra/rotaciona a chave HMAC de um device (segredo 64 hex). */
  registerDeviceKey: (mac: string, payload: DeviceKeyPayload): Promise<{ mac: string, created: boolean }> =>
    apiPost<{ mac: string, created: boolean }>(API_ENDPOINTS.RAIZ_DEVICE_KEY(mac), payload),

  /** Ativa/desativa a chave de um device. */
  setDeviceKeyActive: (mac: string, active: boolean): Promise<{ mac: string, active: boolean }> =>
    apiPatch<{ mac: string, active: boolean }>(API_ENDPOINTS.RAIZ_DEVICE_KEY(mac), { active }),

  // ── Credenciais por device (raiz_iot#47; platform_admin) ────────────────────
  /** Estado das credenciais de cada sensor da empresa (migração). */
  listCredentialsStatus: (): Promise<CredentialsStatusRow[]> =>
    apiGet<CredentialsStatusRow[]>(API_ENDPOINTS.RAIZ_CREDENTIALS_STATUS),

  /** Estado de um sensor; `reveal` traz os valores pedidos (cada um vira evento na trilha). */
  getDeviceCredentials: (mac: string, reveal: RevealField[] = []): Promise<DeviceCredentialsState> =>
    apiGet<DeviceCredentialsState>(
      `${API_ENDPOINTS.RAIZ_DEVICE_CREDENTIALS(mac)}${reveal.length > 0 ? `?reveal=${reveal.join(',')}` : ''}`,
    ),

  /** Troca chave e/ou senhas — o sensor recebe no próximo beacon. */
  rotateDeviceCredentials: (mac: string, fields: CredentialField[]): Promise<{ mac: string, seq: number }> =>
    apiPost<{ mac: string, seq: number }>(API_ENDPOINTS.RAIZ_DEVICE_CREDENTIALS_ROTATE(mac), { fields }),

  /** Descomissionar: desativa a chave (401 daí em diante). */
  revokeDeviceCredentials: (mac: string): Promise<{ mac: string, active: boolean }> =>
    apiDelete<{ mac: string, active: boolean }>(API_ENDPOINTS.RAIZ_DEVICE_CREDENTIALS(mac)),

  /** Senha de instalação da equipe: status (nunca o valor). */
  getInstallPasswordStatus: (): Promise<InstallPasswordStatus> =>
    apiGet<InstallPasswordStatus>(API_ENDPOINTS.RAIZ_FLEET_INSTALL_PASSWORD),

  /** Troca a senha de instalação — um push por sensor. */
  rotateInstallPassword: (password: string): Promise<{ seq: number, pushes: number }> =>
    apiPost<{ seq: number, pushes: number }>(API_ENDPOINTS.RAIZ_FLEET_INSTALL_PASSWORD, { password }),

  // ── Unidades monitoradas (área / caminhão / caixa térmica) ────────────────
  /** Lista de unidades (com contagem de sensores ativos). */
  // ── Sites (instalações) ───────────────────────────────────────────────────
  // Agrupam unidades. A API devolve { sites: [...] } (envelope), ao contrário de
  // listUnits, que devolve o array cru — não uniformizei porque mexer no
  // contrato de units quebraria todo consumidor por um ganho estético.

  /** Escada de níveis do ambiente, do mais baixo para o mais alto. */
  getUnitLevels: (unitId: number): Promise<RaizUnitLevel[]> =>
    apiGet<{ levels: RaizUnitLevel[] }>(API_ENDPOINTS.RAIZ_UNIT_LEVELS(unitId)).then((r) => r.levels),

  /** Substitui a escada INTEIRA (replace, não merge) e a altura útil. */
  setUnitLevels: (unitId: number, body: { height_m?: number | null, levels: Array<{ name: string, z_m: number }> }): Promise<void> =>
    apiPut<void>(API_ENDPOINTS.RAIZ_UNIT_LEVELS(unitId), body),

  /** Vigências da faixa, da mais nova para a mais velha (auditor lê). */
  getUnitBandHistory: (unitId: number): Promise<RaizUnitBandVigencia[]> =>
    apiGet<{ vigencias: RaizUnitBandVigencia[] }>(API_ENDPOINTS.RAIZ_UNIT_BAND_HISTORY(unitId))
      .then((r) => r.vigencias),

  listSites: (): Promise<RaizSite[]> =>
    apiGet<{ sites: RaizSite[] }>(API_ENDPOINTS.RAIZ_SITES).then((r) => r.sites),

  createSite: (payload: SitePayload): Promise<{ id: number }> =>
    apiPost<{ id: number }>(API_ENDPOINTS.RAIZ_SITES, payload),

  updateSite: (id: number, payload: Partial<SitePayload>): Promise<void> =>
    apiPatch<void>(API_ENDPOINTS.RAIZ_SITE(id), payload),

  /** Desativa/reativa. Não existe apagar site: a FK das unidades é RESTRICT. */
  setSiteActive: (id: number, active: boolean): Promise<void> =>
    apiPatch<void>(API_ENDPOINTS.RAIZ_SITE_ACTIVE(id), { active }),

  listUnits: (): Promise<RaizUnit[]> =>
    apiGet<RaizUnit[]>(API_ENDPOINTS.RAIZ_UNITS),

  /** Detalhe da unidade + membros (última leitura + status in-band). */
  getUnit: (id: number): Promise<RaizUnitDetail> =>
    apiGet<RaizUnitDetail>(API_ENDPOINTS.RAIZ_UNIT(id)),

  /** Cria unidade. */
  createUnit: (payload: UnitPayload): Promise<{ id: number }> =>
    apiPost<{ id: number }>(API_ENDPOINTS.RAIZ_UNITS, payload),

  /** Importa lotes/talhões de um KML. `dry_run: true` só devolve o plano (pré-visualização). */
  importUnits: (payload: UnitsImportPayload): Promise<UnitsImportResponse> =>
    apiPost<UnitsImportResponse>(API_ENDPOINTS.RAIZ_UNITS_IMPORT, payload),

  /** Atualiza campos da unidade. */
  updateUnit: (id: number, payload: Partial<UnitPayload>): Promise<void> =>
    apiPatch<void>(API_ENDPOINTS.RAIZ_UNIT(id), payload),

  /** Desativa (soft) a unidade. */
  deactivateUnit: (id: number): Promise<void> =>
    apiDelete<void>(API_ENDPOINTS.RAIZ_UNIT(id)),

  /** Reativa uma unidade inativa (PATCH active:true). */
  reactivateUnit: (id: number): Promise<void> =>
    apiPatch<void>(API_ENDPOINTS.RAIZ_UNIT(id), { active: true }),

  /** Vincula um sensor à unidade. */
  assignDevice: (id: number, device: string): Promise<{ id: number }> =>
    apiPost<{ id: number }>(API_ENDPOINTS.RAIZ_UNIT_DEVICES(id), { device }),

  /** Desvincula (soft) um sensor da unidade. */
  unassignDevice: (id: number, mac: string): Promise<void> =>
    apiDelete<void>(API_ENDPOINTS.RAIZ_UNIT_DEVICE(id, mac)),

  /**
   * Inicia/para o monitoramento do vínculo (issue #21). Parar exige motivo —
   * é o "por quê" da trilha. O primeiro Iniciar é a entrada em serviço.
   */
  // ── Perfis de alarme (issue #15) ──────────────────────────────────────────

  listAlarmProfiles: (): Promise<RaizAlarmProfile[]> =>
    apiGet<{ profiles: RaizAlarmProfile[] }>(API_ENDPOINTS.RAIZ_ALARM_PROFILES).then((r) => r.profiles),

  createAlarmProfile: (name: string, rules: Array<Omit<RaizAlarmRule, 'id'>>): Promise<{ id: number }> =>
    apiPost<{ id: number }>(API_ENDPOINTS.RAIZ_ALARM_PROFILES, { name, rules }),

  updateAlarmProfile: (id: number, patch: { name?: string, active?: boolean, rules?: Array<Omit<RaizAlarmRule, 'id'>> }): Promise<void> =>
    apiPatch<void>(API_ENDPOINTS.RAIZ_ALARM_PROFILE(id), patch),

  getUnitAlarmProfile: (unitId: number): Promise<RaizUnitAlarmProfile> =>
    apiGet<RaizUnitAlarmProfile>(API_ENDPOINTS.RAIZ_UNIT_ALARM_PROFILE(unitId)),

  /** Define o perfil vigente (null = voltar à faixa da unidade). Troca vira versão na trilha. */
  setUnitAlarmProfile: (unitId: number, profileId: number | null): Promise<RaizUnitAlarmProfile> =>
    apiPut<RaizUnitAlarmProfile>(API_ENDPOINTS.RAIZ_UNIT_ALARM_PROFILE(unitId), { profile_id: profileId }),

  setUnitDeviceMonitoring: (id: number, mac: string, action: 'start' | 'stop', reason?: string): Promise<{ state: string, monitoring_since: string | null }> =>
    apiPost<{ state: string, monitoring_since: string | null }>(
      API_ENDPOINTS.RAIZ_UNIT_DEVICE_MONITORING(id, mac),
      { action, ...(reason ? { reason } : {}) },
    ),

  /** Posiciona (0..1 × 0..1) ou remove (null) um sensor na planta da unidade. */
  setMemberPosition: (unitId: number, mac: string, position: FloorPosition | null): Promise<void> =>
    apiPatch<void>(API_ENDPOINTS.RAIZ_UNIT_DEVICE_POSITION(unitId, mac), { position }),

  /**
   * URL direta da imagem da planta — vai num <img src> (o cookie de sessão
   * acompanha por ser same-origin). `version` é o cache-buster: use
   * updated_at + um contador incrementado após upload/remoção.
   */
  floorplanUrl: (unitId: number, version: string): string =>
    `${API_ENDPOINTS.RAIZ_UNIT_FLOORPLAN(unitId)}?v=${encodeURIComponent(version)}`,

  /**
   * Envia a imagem da planta (PUT binário — o wrapper JSON não serve aqui:
   * o corpo é o próprio File e o Content-Type é o do arquivo). 413/415 ganham
   * mensagens específicas; demais erros tentam a mensagem do servidor.
   */
  uploadFloorplan: async (unitId: number, file: File): Promise<void> => {
    let res: Response
    try {
      res = await fetch(API_ENDPOINTS.RAIZ_UNIT_FLOORPLAN(unitId), {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      })
    } catch {
      throw new ApiError(0, 'Sem conexão com o servidor.')
    }
    if (!res.ok) {
      if (res.status === 413) throw new ApiError(413, FLOORPLAN_TOO_LARGE_MSG)
      if (res.status === 415) throw new ApiError(415, FLOORPLAN_BAD_TYPE_MSG)
      let message = ''
      try {
        const text = await res.text()
        if (text) {
          try {
            const parsed = JSON.parse(text) as { message?: unknown }
            if (typeof parsed?.message === 'string') message = parsed.message
          } catch { /* corpo não-JSON */ }
        }
      } catch { /* corpo ilegível */ }
      throw new ApiError(res.status, message || 'Falha ao enviar a planta.')
    }
  },

  /** Remove a imagem da planta da unidade (as posições dos sensores ficam). */
  deleteFloorplan: (unitId: number): Promise<void> =>
    apiDelete<void>(API_ENDPOINTS.RAIZ_UNIT_FLOORPLAN(unitId)),

  /** Relatório (rollup) da unidade na janela (datas YYYY-MM-DD ou ISO). */
  getUnitReport: (id: number, from: string, to: string): Promise<RaizUnitReport> =>
    apiGet<RaizUnitReport>(withQuery(API_ENDPOINTS.RAIZ_UNIT_REPORT(id), { from, to })),

  /** Eventos de excursão da unidade (abertos + histórico). */
  listExcursions: (id: number): Promise<RaizExcursion[]> =>
    apiGet<RaizExcursion[]>(API_ENDPOINTS.RAIZ_UNIT_EXCURSIONS(id)),

  /** Detecta excursões na janela (deriva + UPSERT idempotente). */
  detectExcursions: (id: number, from: string, to: string): Promise<{ detected: number }> =>
    apiPost<{ detected: number }>(
      `${API_ENDPOINTS.RAIZ_UNIT_EXCURSIONS_DETECT(id)}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`, {}),

  /** Fila global de excursões (todas as unidades). `status` aceita lista CSV (ex.: 'open,under_investigation'). */
  listAllExcursions: (params: { status?: string, device?: string, limit?: number } = {}): Promise<RaizGlobalExcursion[]> =>
    apiGet<RaizGlobalExcursion[]>(withQuery(API_ENDPOINTS.RAIZ_EXCURSIONS, params)),

  /** Resumo p/ o sino do topbar e card do dashboard: abertas + em investigação + última abertura. */
  getExcursionsSummary: (): Promise<RaizExcursionsSummary> =>
    apiGet<RaizExcursionsSummary>(API_ENDPOINTS.RAIZ_EXCURSIONS_SUMMARY),

  /** Atualiza a disposição (status + causa/ação/impacto) de uma excursão. */
  updateExcursion: (eid: number, payload: ExcursionDispositionPayload): Promise<void> =>
    apiPatch<void>(API_ENDPOINTS.RAIZ_EXCURSION(eid), payload),

  /** Trilha de disposição da excursão (valor antigo/novo + hash encadeado, 1825). */
  getExcursionAudit: (eid: number): Promise<RaizExcursionAuditEntry[]> =>
    apiGet<RaizExcursionAuditEntry[]>(API_ENDPOINTS.RAIZ_EXCURSION_AUDIT(eid)),

  // ── Lab de Bateria (spec 2026-10-21) ─────────────────────────────────────
  /** Frota: sem scope = plataforma (todas as empresas); 'company' = só a empresa ativa. */
  batteryFleet: (window: '7d' | '30d', scope?: 'company'): Promise<BatteryFleet> =>
    apiGet<BatteryFleet>(withQuery(API_ENDPOINTS.RAIZ_BATTERY_FLEET, { window, scope })),
  /** Curva de descarga de um device (plataforma vê qualquer um; empresa só os dela). */
  batteryCurve: (mac: string, window: BatteryLabWindow): Promise<BatteryCurve> =>
    apiGet<BatteryCurve>(withQuery(API_ENDPOINTS.RAIZ_BATTERY_CURVE(mac), { window })),

  // ── FOTA v1 (Console de Frota — gestão de firmware over-the-air) ────────────
  /** Releases publicadas (todas as boards; a `active` de cada board é a ofertada). */
  listFirmwareReleases: (): Promise<FirmwareRelease[]> =>
    apiGet<{ releases: FirmwareRelease[] }>(API_ENDPOINTS.RAIZ_FIRMWARE_RELEASES).then((r) => r.releases),

  /**
   * Publica uma release: o corpo é os próprios bytes do .bin (octet-stream — o
   * wrapper JSON não serve) e board/versao/mandatory/min_current_version/
   * requires_device_creds vão na query string (undefined fica de fora: sem
   * requires_device_creds, o servidor aplica o default por versão). 400 (semver/board inválidos, ou
   * piso abaixo da 3.50.0 em release marcada — o `detail` do servidor diz
   * qual), 409 (versão+board já existem) e 413 (binário grande demais) ganham
   * mensagens específicas. O 201 traz o gate e o piso como o SERVIDOR os gravou.
   */
  uploadFirmwareRelease: async ({ board, versao, mandatory, min_current_version, requires_device_creds, file }: FirmwareUploadParams): Promise<FirmwareUploadResult> => {
    const url = withQuery(API_ENDPOINTS.RAIZ_FIRMWARE_RELEASES, {
      board, versao, mandatory, min_current_version, requires_device_creds,
    })
    let res: Response
    try {
      res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: file,
      })
    } catch {
      throw new ApiError(0, 'Sem conexão com o servidor.')
    }
    if (!res.ok) {
      if (res.status === 409) throw new ApiError(409, FIRMWARE_RELEASE_EXISTS_MSG)
      if (res.status === 413) throw new ApiError(413, FIRMWARE_RELEASE_TOO_LARGE_MSG)
      let message = ''
      let detail = ''
      try {
        const text = await res.text()
        if (text) {
          try {
            const parsed = JSON.parse(text) as { message?: unknown, detail?: unknown }
            if (typeof parsed?.message === 'string') message = parsed.message
            if (typeof parsed?.detail === 'string') detail = parsed.detail
          } catch { /* corpo não-JSON */ }
        }
      } catch { /* corpo ilegível */ }
      // O 400 do gate (#117) diz o que recusou: piso abaixo da 3.50.0 numa
      // release marcada não é "verifique a versão e o board".
      if (res.status === 400) throw new ApiError(400, detail ? `Dados inválidos — ${detail}` : FIRMWARE_RELEASE_INVALID_MSG)
      throw new ApiError(res.status, message || 'Falha ao publicar a release.')
    }
    return (await res.json()) as FirmwareUploadResult
  },

  /**
   * Ativa uma release: passa a ser a ofertada ao board no próximo check-in.
   * Release marcada (issue #117) passa antes pelo censo do board: havendo
   * devices que ela não alcança, o servidor responde 409 `creds_census` e NÃO
   * ativa. `confirmCensus` é o "ative mesmo assim" — molde do confirmUnknown
   * do canário.
   */
  activateFirmwareRelease: (id: number, confirmCensus = false): Promise<void> =>
    apiPost<void>(API_ENDPOINTS.RAIZ_FIRMWARE_RELEASE_ACTIVATE(id), confirmCensus ? { confirm_creds_census: true } : {}),

  /**
   * O FREIO: para de ofertar a release, sem ofertar outra no lugar. Também
   * interrompe a DISTRIBUIÇÃO do binário (o server passa a devolver 404).
   * Não desinstala nada de quem já recebeu.
   */
  deactivateFirmwareRelease: (id: number): Promise<void> =>
    apiPost<void>(API_ENDPOINTS.RAIZ_FIRMWARE_RELEASE_DEACTIVATE(id), {}),

  /**
   * Retoma uma release FREIADA pelo freio de frota (issue #7). O freio parou de
   * ofertar sozinho após uma onda de rollback; retomar volta a ofertar e o
   * detector só reconta falhas a partir daqui (não re-freia com o dado antigo).
   */
  resumeFirmwareRelease: (id: number): Promise<void> =>
    apiPost<void>(API_ENDPOINTS.RAIZ_FIRMWARE_RELEASE_RESUME(id), {}),

  /**
   * Retoma a oferta suspensa por falha repetida para UM device (issue #122).
   * O servidor grava um marco `offer_resumed` na trilha e a contagem de falhas
   * passa a valer só daí em diante; a oferta sai no próximo check-in. Não
   * alcança o freio da própria placa (blacklist/cooloff na NVS). Platform-only.
   * 409 `device_not_suppressed` quando não há o que retomar — inclusive com
   * `reason: storage_downgrade_blocked` (#126), quando a placa está no anel e a
   * release ativa do board é anterior à do anel: retomar não entregaria nada.
   */
  resumeFirmwareDeviceOffer: (mac: string, reason: string | null): Promise<FirmwareDeviceOfferResume> =>
    apiPost<FirmwareDeviceOfferResume>(API_ENDPOINTS.RAIZ_FIRMWARE_DEVICE_RESUME(mac), reason ? { reason } : {}),

  /**
   * Canário por release: restringe a oferta aos MACs da lista; `null` libera
   * para a frota inteira do board ("Liberar para a frota"). Não ativa nem
   * desativa nada — canário é escopo, não estado. O servidor normaliza e
   * deduplica os MACs e audita (canary_set / canary_cleared).
   */
  /**
   * `confirmUnknown` = "grave mesmo com MAC fora da frota" (issue #112). Sem
   * ele o servidor responde 409 `canary_unknown_macs` e NÃO grava — é o que faz
   * o aviso chegar antes de confirmar.
   */
  setFirmwareReleaseCanary: (id: number, macs: string[] | null, confirmUnknown = false): Promise<FirmwareReleaseCanary> =>
    apiPatch<FirmwareReleaseCanary>(API_ENDPOINTS.RAIZ_FIRMWARE_RELEASE_CANARY(id),
      confirmUnknown ? { macs, confirm_unknown: true } : { macs }),

  /** Status FOTA da frota (versão atual, slot livre, última oferta/resultado por device). */
  listFirmwareDeviceStatus: (): Promise<FirmwareDeviceStatus[]> =>
    apiGet<{ devices: FirmwareDeviceStatus[] }>(API_ENDPOINTS.RAIZ_FIRMWARE_DEVICES).then((r) => r.devices),

  /**
   * Trilha de auditoria FOTA. Append-only por DISCIPLINA DE APLICAÇÃO: nada no
   * produto altera nem remove entrada. Não dizemos "imutável" — o app conecta
   * como owner da tabela, e REVOKE não vincula owner (limitação declarada).
   * `limit` é clampado no servidor em [1, 500] (default 100).
   */
  listFirmwareAudit: (limit = 100): Promise<FirmwareAuditEntry[]> =>
    apiGet<{ audit: FirmwareAuditEntry[] }>(`${API_ENDPOINTS.RAIZ_FIRMWARE_AUDIT}?limit=${limit}`).then((r) => r.audit),

  /**
   * Corrige um registro da trilha por APOSIÇÃO: anexa uma anotação que o
   * referencia, sem tocar no original. O autor é a sessão (o servidor ignora
   * qualquer autor no corpo). É como se corrige trilha append-only.
   */
  annotateFirmwareAudit: (id: number, statement: string, reason: string): Promise<{ id: number }> =>
    apiPost<{ id: number }>(API_ENDPOINTS.RAIZ_FIRMWARE_AUDIT_ANNOTATION(id), { statement, reason }),

  // ── Bancada (Cenário A — flash de fresh device por USB/WebSerial) ───────────
  /**
   * Anexa a imagem de FÁBRICA (merged bin) a uma release — corpo = os próprios
   * bytes (octet-stream; o wrapper JSON não serve). Write-once: 409 se a release
   * já tem imagem. 201 { sha256, tamanho }.
   */
  uploadFactoryImage: async (id: number, file: File): Promise<{ sha256: string, tamanho: number }> => {
    let res: Response
    try {
      res = await fetch(API_ENDPOINTS.RAIZ_FIRMWARE_RELEASE_FACTORY(id), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: file,
      })
    } catch {
      throw new ApiError(0, 'Sem conexão com o servidor.')
    }
    if (!res.ok) {
      if (res.status === 409) throw new ApiError(409, FACTORY_EXISTS_MSG)
      if (res.status === 413) throw new ApiError(413, FACTORY_TOO_LARGE_MSG)
      if (res.status === 400) throw new ApiError(400, FACTORY_INVALID_MSG)
      let message = ''
      try {
        const text = await res.text()
        if (text) {
          try {
            const parsed = JSON.parse(text) as { message?: unknown }
            if (typeof parsed?.message === 'string') message = parsed.message
          } catch { /* corpo não-JSON */ }
        }
      } catch { /* corpo ilegível */ }
      throw new ApiError(res.status, message || 'Falha ao anexar a imagem de fábrica.')
    }
    return (await res.json()) as { sha256: string, tamanho: number }
  },

  /**
   * Baixa a imagem de FÁBRICA da release (bytes) + o sha256 do header
   * X-RAIZ-SHA256, para o flasher de bancada gravar via WebSerial. Usa fetch
   * direto (o corpo é binário). 404 → imagem não anexada.
   */
  fetchFactoryImage: async (id: number): Promise<FactoryImage> => {
    let res: Response
    try {
      res = await fetch(API_ENDPOINTS.RAIZ_FIRMWARE_RELEASE_FACTORY_BIN(id), {
        credentials: 'include',
        headers: { Accept: 'application/octet-stream' },
      })
    } catch {
      throw new ApiError(0, 'Sem conexão com o servidor.')
    }
    if (!res.ok) {
      if (res.status === 404) throw new ApiError(404, FACTORY_MISSING_MSG)
      throw new ApiError(res.status, res.status >= 500 ? 'Erro no servidor ao baixar a imagem.' : 'Falha ao baixar a imagem de fábrica.')
    }
    const buf = await res.arrayBuffer()
    return { bytes: new Uint8Array(buf), sha256: res.headers.get('X-RAIZ-SHA256') }
  },

  /** Registra o resultado de um flash de bancada na trilha (autor = sessão). */
  recordBenchFlash: (payload: BenchFlashPayload): Promise<{ id: number }> =>
    apiPost<{ id: number }>(API_ENDPOINTS.RAIZ_FIRMWARE_BENCH_FLASH, payload),
}
